import { type Subproject as CardSubproject } from '@/organization/landing/components/projectCard/types';
import {
  getOverallStatus,
  getSequentialBarStatuses,
  getSubprojectStatusStyle,
} from '@/organization/landing/components/projectCard/utils';
import { getSubprojectStatusIcon } from '@/organization/landing/statusUtils';
import { type Subproject } from '@/stores/SubprojectStore';
import { alpha, Box, Typography, useTheme } from '@mui/material';

const FONT_HEADER = 'Montserrat, sans-serif';

const STAGE_LABELS = ['Space Usage', 'SubSystems', 'Energy Sources'];

function toCardSubproject(s: Subproject): CardSubproject {
  return s as unknown as CardSubproject;
}

interface SubprojectDropdownCardProps {
  subproject: Subproject;
  isSelected: boolean;
}

export function SubprojectDropdownCard({
  subproject,
  isSelected,
}: SubprojectDropdownCardProps) {
  const theme = useTheme();
  const status = subproject.pencilsDown
    ? 'pencils-down'
    : getOverallStatus(toCardSubproject(subproject));
  const style = getSubprojectStatusStyle(status);
  const bars = getSequentialBarStatuses(toCardSubproject(subproject));

  return (
    <Box
      sx={{
        borderRadius: '6px',
        overflow: 'hidden',
        width: '100%',
        border: `1px solid ${alpha(theme.palette.common.black, 0.08)}`,
      }}
    >
      {/* Header: name + status pill */}
      <Box
        sx={{
          bgcolor: isSelected ? 'info.main' : 'background.default',
          px: 2.5,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 4,
        }}
      >
        <Typography
          sx={{
            fontFamily: FONT_HEADER,
            fontWeight: 600,
            fontSize: 12,
            color: 'text.primary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {subproject.name}
        </Typography>
        <Box
          sx={{
            bgcolor:
              status === 'finished'
                ? 'primary.main'
                : status === 'in-progress'
                  ? 'secondary.main'
                  : status === 'pencils-down'
                    ? style.bg
                    : 'background.primary',
            color:
              status === 'pencils-down'
                ? style.color
                : status === 'in-progress'
                  ? 'primary.main'
                  : 'text.primary',
            borderRadius: '10px',
            px: 2,
            py: 1,
            fontSize: 9,
            fontWeight: 600,
            fontFamily: FONT_HEADER,
            whiteSpace: 'nowrap',
            lineHeight: 1.4,
          }}
        >
          {style.label}
        </Box>
      </Box>
      {/* Stage completion pills */}
      <Box
        sx={{
          bgcolor: isSelected ? 'primary.light' : 'primary.main',
          px: 2.5,
          py: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        {(['bar1', 'bar2', 'bar3'] as const).map((barKey, i) => (
          <Box
            key={barKey}
            component='img'
            src={getSubprojectStatusIcon(bars[barKey])}
            alt={STAGE_LABELS[i]}
            sx={{
              flex: 1,
              height: 9,
              display: 'block',
              imageRendering: 'crisp-edges',
            }}
          />
        ))}
      </Box>
    </Box>
  );
}
